/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { hooks as commonHooks } from '@sitebuilder/common';
import { StateShape as ReduxStateShape } from '../../reducers';
import { actionCreators } from '../../reducers/contentList';
import AdminMainPanel from '../Layouts/AdminMainPanel';
import { ItemsTable } from '../BaseComponents';
import NavBar from '../Layouts/NavBar';
import { PageActionOption, contentListActionOptions } from '../Layouts/LeftSidebar/NavOptions';
import ContentListRightSidebar from './ContentListRightSidebar';

const ContentListComponent = () => {
    const params: any = useParams();
    const { contentListId } = params;
    const dispatch = useDispatch();
    const { contentLists, selectedContentList, showDeletedItems } = useSelector(
        (state: ReduxStateShape) => state.contentList
    );
    const [pageAction, setPageAction] = useState('');
    const [urlState, setUrlState] = useState<string>('');

    useEffect(() => {
        if (contentLists === null) {
            if (contentListId !== null) {
                dispatch(actionCreators.listContentLists(contentListId));
            } else {
                dispatch(actionCreators.listContentLists(''));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentListId]);

    useEffect(() => {
        // prevent calling api on each and every render
        // call only when `showDeletedItems` is true
        if (showDeletedItems) {
            if (contentListId !== null) {
                dispatch(actionCreators.listContentLists(urlState || contentListId));
            } else {
                dispatch(actionCreators.listContentLists(urlState || ''));
            }
        }
    }, [dispatch, showDeletedItems, contentListId, urlState]);

    const onClickRow = (contentListId: string, isSelected: boolean) => {
        if (isSelected) {
            dispatch(actionCreators.unselectContentList([contentListId]));
        } else {
            dispatch(actionCreators.setSelectedContentList([contentListId]));
        }
        setPageAction('');
    };

    const onClickNewContentList = () => {
        dispatch(actionCreators.unselectAllContentList());
        setPageAction(pageAction === 'content list' ? '' : 'content list');
    };

    const onClickNewFolder = () => {
        dispatch(actionCreators.unselectAllContentList());
        setPageAction(pageAction === 'folder' ? '' : 'folder');
    };

    const i18n = commonHooks.useLocalization();
    const showRightSidebar =
        pageAction === 'content list' || pageAction === 'folder' || selectedContentList.length > 0;

    return (
        <div className="content-list">
            <AdminMainPanel>
                <NavBar
                    rightSidebar={
                        <ContentListRightSidebar
                            addNewContentList={pageAction === 'content list'}
                            urlState={urlState}
                            addNewFolder={pageAction === 'folder'}
                        />
                    }
                    hideRightSidebar={!showRightSidebar}
                />
                <div className="admin-main-content content-list-panel">
                    <ItemsTable
                        tableType={'contentList'}
                        redirectBaseUrl={'/admin/content-list'}
                        items={contentLists}
                        selectedItems={selectedContentList}
                        onClickRow={onClickRow}
                        onUrlStateChange={(urlState: string) => setUrlState(urlState)}
                        title={i18n.t('contentList_title')}
                        subtitle={i18n.t('contentList_subtitle')}
                        showDeletedItems={showDeletedItems}
                        onClickShowDeletedItems={() =>
                            dispatch(actionCreators.setShowDeletedItems())
                        }
                        options={contentListActionOptions}
                        selectedOption={contentListActionOptions.find((o) => o.name === pageAction)}
                        onClickOption={(opt: PageActionOption) => {
                            if (opt.name === 'folder') {
                                onClickNewFolder();
                            } else if (opt.name === 'content list') {
                                onClickNewContentList();
                            }
                        }}
                    />
                </div>
            </AdminMainPanel>
        </div>
    );
};

export default ContentListComponent;
